const cartRecommendationsCache = new Map();
let lastCartRecommendations = null;

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    const key = `${this.dataset.url}|${this.dataset.cartKey}`;

    if (cartRecommendationsCache.has(key)) {
      this.render(cartRecommendationsCache.get(key));
      return;
    }
    // Avoid flashing the fallback collection while the new list loads
    if (lastCartRecommendations) this.render(lastCartRecommendations);

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, "text/html");
        const section = html.querySelector(".shopify-section");
        // Keep the section's stylesheet links along with the cross-sell markup
        if (!section?.querySelector(".wt-cart__cross-sell")) return;
        cartRecommendationsCache.set(key, section.innerHTML);
        lastCartRecommendations = section.innerHTML;
        if (this.isConnected) this.render(section.innerHTML);
      })
      .catch((e) => console.error(e));
  }

  render(markup) {
    this.innerHTML = markup;
    if (document.body.classList.contains("page-overlay-cart-on")) {
      setTabindex(this.querySelectorAll("a, button, input, select"), "0");
    }
  }
}

if (!customElements.get("cart-recommendations")) {
  customElements.define("cart-recommendations", CartRecommendations);
}

class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.input = this.querySelector("input");
    this.button = this.querySelector("button[type=submit]");
    this.error = this.querySelector(".wt-cart__discount__error");
    this.list = this.querySelector(".wt-cart__discount__codes");
    this.template = this.querySelector(".wt-cart__discount__template");

    // Liquid's cart.discount_codes leaves out codes that don't apply, so read them from cart.js
    fetch(`${window.Shopify.routes.root}cart.js`)
      .then((response) => response.json())
      .then((cart) => this.renderCodes(cart.discount_codes))
      .catch((e) => console.error(e));

    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      const code = this.input.value.trim();
      if (!code) return;
      this.updateCodes((codes) => [...codes.filter((c) => c.toLowerCase() !== code.toLowerCase()), code]);
    });

    this.addEventListener("click", (e) => {
      const remove = e.target.closest("[data-remove-code]");
      if (!remove) return;
      const code = remove.dataset.removeCode;
      this.updateCodes((codes) => codes.filter((c) => c !== code));
    });
  }

  renderCodes(discountCodes) {
    this.list.replaceChildren(
      ...discountCodes.map((discountCode) => {
        const item = this.template.content.firstElementChild.cloneNode(true);
        item.querySelector(".wt-cart__discount__name").textContent = discountCode.code;
        item.querySelector("[type=button]").dataset.removeCode = discountCode.code;
        if (!discountCode.applicable) {
          item.classList.add("wt-cart__discount__code--invalid");
          item.querySelector(".wt-cart__discount__note").hidden = false;
        }
        return item;
      }),
    );
    this.list.hidden = discountCodes.length === 0;
  }

  updateCodes(getNewCodes) {
    this.button.setAttribute("aria-disabled", true);
    this.error.hidden = true;

    fetch(`${window.Shopify.routes.root}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        const codes = getNewCodes(cart.discount_codes.map((d) => d.code));
        return fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ discount: codes.join(",") }),
        });
      })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
      })
      .catch((e) => {
        console.error(e);
        this.error.textContent = this.dataset.error;
        this.error.hidden = false;
        this.button.removeAttribute("aria-disabled");
      });
  }
}

if (!customElements.get("cart-discount")) {
  customElements.define("cart-discount", CartDiscount);
}

class CartCrossSellCard extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.select = this.querySelector("select");
    this.button = this.querySelector("button[type=submit]");

    this.select?.addEventListener("change", () => {
      const option = this.select.selectedOptions[0];
      const price = this.querySelector("[data-cs-price]");
      price.replaceChildren();
      if (option.dataset.comparePrice) {
        const compare = document.createElement("s");
        compare.textContent = option.dataset.comparePrice;
        price.append(compare);
      }
      const current = document.createElement("span");
      current.textContent = option.dataset.price;
      price.append(current);
      if (option.dataset.image) this.querySelector("[data-cs-image]").src = option.dataset.image;
    });

    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.button.setAttribute("aria-disabled", true);
      fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: Number(this.form.elements.id.value), quantity: 1 }] }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
        })
        .catch((e) => {
          console.error(e);
          this.button.removeAttribute("aria-disabled");
        });
    });
  }
}

if (!customElements.get("cart-cross-sell-card")) {
  customElements.define("cart-cross-sell-card", CartCrossSellCard);
}

// Instant page-builder buttons (e.g. /pages/gift-guide) are set to "Add to Instant cart" or
// "Redirect to cart". Without an Instant cart on the page both send shoppers to /cart, so add
// the item here and open the theme drawer instead.
const INSTANT_CART_ACTIONS = ["add-to-cart", "add-to-instant-cart", "redirect-to-cart"]
  .map((type) => `[data-instant-action-type="${type}"]`)
  .join(", ");

window.addEventListener(
  "click",
  (e) => {
    const button = e.target.closest?.(INSTANT_CART_ACTIONS);
    const drawer = document.querySelector("cart-drawer");
    if (!button || !drawer || drawer.dataset.cartType !== "drawer") return;
    if (document.querySelector('.__instant[data-instant-layout="CART"]')) return;

    const form = button.closest("[data-instant-form-product-url]");
    if (!form || button.getAttribute("disabled") === "disabled" || button.dataset.instantDisabled === "true") return;
    // Leave anything more involved (line item properties, subscriptions, bundles, discount links) to Instant
    if (
      button.dataset.instantDiscountCode ||
      button.dataset.instantClearCart === "true" ||
      form.querySelector('[name^="properties["], [name$="__selling-plan-id"]')
    ) {
      return;
    }

    const variantId = form.dataset.instantFormVariantId || button.dataset.instantActionVariantId;
    if (!variantId) return;
    const quantity =
      form.dataset.instantSelectedQuantity ||
      form.querySelector('[data-instant-dynamic-content-source="QUANTITY_SELECT"]')?.value ||
      "1";

    e.preventDefault();
    e.stopImmediatePropagation();
    button.setAttribute("aria-busy", "true");

    fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: Number(quantity) }],
        sections: drawer.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          alert(data.description || data.message);
          return;
        }
        drawer.renderContents(data, !drawer.isOpen);
      })
      .catch((error) => {
        console.error(error);
        window.location.href = button.href || window.routes.cart_url;
      })
      .finally(() => button.removeAttribute("aria-busy"));
  },
  true,
);
